import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Bell, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import type { Event } from "./EventCard";
import type { Id } from "convex/_generated/dataModel";

interface RemindMeModalProps {
  event: Event;
  onClose: () => void;
}

// Matches the validation used by EventSubmitForm.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email capture for "remind me about this event".
 *
 * Deliberately Clerk-free — it reaches `useCurrentUser`, not `useUser`, because
 * when no Clerk key is configured there is no ClerkProvider in the tree at all
 * and Clerk hooks throw. Signed-in visitors get their address prefilled;
 * everyone else just types one.
 */
export function RemindMeModal({ event, onClose }: RemindMeModalProps) {
  const { user, isLoggedIn } = useCurrentUser();
  const createReminder = useMutation(api.reminders.createReminder);

  const [email, setEmail] = useState(user?.email ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createReminder({
        eventId: event._id as Id<"events">,
        email: trimmed,
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message.replace(/^.*Uncaught Error:\s*/, "").split("\n")[0]!
          : "Something went wrong. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl p-6 mx-4 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Check className="w-4 h-4 text-primary" />
              <h2 className="font-semibold text-foreground">Reminder set</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              We'll email you the day before{" "}
              <span className="text-foreground">{event.title}</span>. Every
              reminder includes a link to cancel it — no account needed.
            </p>
            <Button onClick={onClose} className="w-full">
              Done
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-4 h-4 text-primary" />
              <h2 className="font-semibold text-foreground">Remind me</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              We'll send one email the day before{" "}
              <span className="text-foreground">{event.title}</span>. Nothing
              else.
            </p>

            <Label htmlFor="reminder-email" className="mb-1.5 block">
              Email address
            </Label>
            <Input
              id="reminder-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus={!isLoggedIn}
              autoComplete="email"
            />

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2 mt-3">
                {error}
              </p>
            )}

            <div className="flex gap-2 mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? "Setting..." : "Remind me"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
